import {
    ChangeDetectionStrategy,
    Component,
    computed,
    signal,
    viewChild,
    inject,
    ViewEncapsulation,
    OnInit,
} from '@angular/core';
import { FiTodoHelperService } from '../../fi-todo-helper.service';
import { FiTodoListStoreService } from '../fi-todo-list-editor-store.service';
import { FiContentLanguageSharedService } from '@fi-app/shared/fi-content-language-shared/fi-content-language-shared.service';
import { FiValidatingFormDirective } from '@fi-shared/fi-directives/fi-validating-form/fi-validating-form.directive';
import { ObjectTypes } from '@fi-shared/types';
import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';
import { cloneDeep } from '@fi-app/shared/utils/clone-deep';

@Component({
    selector: 'fi-todo-item-edit',
    templateUrl: './fi-todo-item-edit.component.html',
    styleUrls: ['./fi-todo-item-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-todo-item-edit' },
    standalone: false,
})
export class FiTodoItemEditComponent implements OnInit {
    private fiTodoListStoreService = inject(FiTodoListStoreService);
    private fiTodoHelperService = inject(FiTodoHelperService);
    private fiContentLanguageService = inject(FiContentLanguageSharedService);
    private saveChangesWarningService = inject(SaveChangesWarningService);

    protected selectedLanguageId = computed(() => this.fiTodoListStoreService.selectedLanguageId());
    protected availableContentLanguages = this.fiContentLanguageService.getContentLanguages();
    protected todoItem = cloneDeep(this.fiTodoListStoreService.currentTodoListItem());

    protected selectedTabIndex = signal(0);

    private _todoItemEditorForm = viewChild<FiValidatingFormDirective>('todoItemForm');

    public ngOnInit(): void {
        this.startWarningChanges();
    }

    private startWarningChanges(): void {
        this.saveChangesWarningService.startForMultipleObjects([
            (isReceivingInitialObject) =>
                isReceivingInitialObject
                    ? this.fiTodoListStoreService.initialTodoListData()
                    : this.fiTodoListStoreService.currentTodoListData(),
            () => this.todoItem,
        ]);
        this.saveChangesWarningService.preventParentStateObjCheck = true;
        this.saveChangesWarningService.parentStateObjIndexes = [0];
    }

    protected getTodoItemName(): string {
        return this.todoItem?.Names.find((name) => name.ContentLanguageId === this.selectedLanguageId())?.Value;
    }

    protected navigateToSectionsEditor(): void {
        this.fiTodoHelperService.navigateToSectionsEditor();
    }

    protected setSelectedLanguageId(langId: number): void {
        this.fiTodoListStoreService.setSelectedLanguageId(langId);
    }

    protected saveTodoItem(): void {
        this.saveChangesWarningService.stop();
        this.fiTodoListStoreService.setCurrentTodoListItem(this.todoItem);
        this.fiTodoHelperService.saveTodoItem();
    }

    protected submitTodoItemForm(): void {
        this._todoItemEditorForm()?.submit();
    }

    protected handleTodoItemEditorFormError($event): void {
        this.fiTodoHelperService.showAllItemsMandatoryErrorDialog();
    }

    protected readonly ObjectTypes = ObjectTypes;
}
